import React from 'react';
import LaCartePage from '../../components/m/lacarte/LaCartePage';
import { toLaCarteModel } from '../../components/m/lacarte/laCarteModel';
import type { MenuPublicWine } from '../../types/menu';

/**
 * Customer-facing menu — LA CARTE design (approved Direction A).
 *
 * This page is now a thin adapter: the public DTO from
 * /api/public/menu/:slug (or the unified resolver at /api/public/m/:slug
 * when kind === 'menu') is normalized by toLaCarteModel and rendered by the
 * shared LaCartePage, the same renderer the legacy wine-list kind routes
 * through. See src/components/m/lacarte/.
 *
 * Contract notes preserved from the previous renderer:
 * - Prices are rendered exactly as the backend provides them (MAJOR units) —
 *   this view never divides by 100.
 * - Scores are intentionally NOT rendered (licensing) — ignored even if the
 *   backend still emits them.
 * - Admin-only fields (binNumber, inventoryItemId) are
 *   never present in the DTO by backend projection.
 */

interface PublicMenuFormat {
  name: string;
  formatKind: 'VOLUME' | 'COURSE' | 'UNIT' | 'PORTION';
  volumeMl: number | null;
  price: number | null;
}

interface PublicMenuItem {
  id: string;
  name: string | null;
  subtitle: string | null;
  basePrice: number | null;
  currency: string | null;
  notes: string | null;
  externalUrl: string | null;
  formats: PublicMenuFormat[];
  kind?: 'WINE';
  wine?: MenuPublicWine | null;
}

interface PublicMenuSection {
  id: string | null;
  name: string;
  description: string | null;
  items: PublicMenuItem[];
}

export interface PublicMenuPayload {
  menu: {
    slug: string;
    name: string;
    type: string;
    description: string | null;
    venueName: string | null;
    venueLogoUrl: string | null;
    defaultCurrency: string | null;
  };
  sections: PublicMenuSection[];
}

const PublicMenu: React.FC<{ data: PublicMenuPayload }> = ({ data }) => (
  <LaCartePage model={toLaCarteModel('menu', data)} />
);

export default PublicMenu;
